import { z } from 'zod';

/**
 * "Loyalty-redeem" (hűségpont-beváltás) wizard-szerződés — E-105 Phase 3, Wave-2 BE-2.
 *
 * A bérlés-wizardban a partner hűségpontot válthat be a bérlési végösszeg csökkentésére.
 * Ez a DTO a sequential seam ELSŐ PR-je (D-5.2 #5: DTO → consumer, NEM párhuzamos edit);
 * az FE-E csúszka ezt fogyasztja, a reserve/commit endpoint (PR-2) erre épül.
 *
 * INVARIÁNSOK (ADR-103 Amendment-5 D-5.2, RATIFIED 2026-06-03 Winston):
 *  1. reserve-vs-spend: a wizard CSAK reserve/preview-t kér (nem spend); a tényleges
 *     pont-levonás contract-commit-kor ($transaction). A spend-idempotency a VÉGLEGES
 *     `rentalId`-re kulcsolódik (NEM a draftId-re) — különben a draft→rental flip után
 *     egy re-submit dupla-spend-et nyitna (Winston kötelező finomítás).
 *  2. pont = Int (Partner.loyaltyPoints count); a beváltott kedvezmény AMOUNT = Decimal
 *     (HUF) — a wire-en STRING-ként utazik (Prisma Decimal→JSON NEM number → financial
 *     silent float-drift guard, Murat L2 #1). A pont marad number/Int.
 *  3. külön `LOYALTY_REDEEM` pricing-sor (NEM olvad a wizard-store automata-tier-logikába).
 *  4. 50%-cap: a beváltás max a bruttó bérlési összeg 50%-a (a POS-flow reuse-olt szabálya).
 *  5. NEM a `POST /pos/redeem-loyalty` reuse (az `transactionId` POS-context); ez a wizard
 *     `(partnerId, draftId)` reserve-kontextusa → új endpoint (PR-2).
 *
 * @see ADR-103-amendment-5-wave2-be-invariants.md (D-5.2)
 * @see pos.controller.ts redeemLoyalty (a 50%-cap + pont-modell referencia, NEM reuse)
 */

// HUF összeg a wire-en: STRING, decimal-drift guard (NEM number — Prisma Decimal→JSON).
// Nem-negatív, max 2 tizedes (HUF általában egész, de a Decimal-oszlop megengedi).
const LoyaltyAmountSchema = z
  .string()
  .regex(/^\d+(\.\d{1,2})?$/, 'amount nem-negatív decimal string (max 2 tizedes)');

// FE-E csúszka → reserve-preview kérés. A szerver a JWT-ből deriválja a tenantId-t.
export const LoyaltyRedeemReserveRequestSchema = z.object({
  partnerId: z.string().uuid(),
  draftId: z.string().min(1),
  // Hány pontot szeretne beváltani (a csúszka értéke) — pozitív egész.
  pointsToRedeem: z.number().int().positive(),
  // A bruttó bérlési összeg (HUF) amihez az 50%-cap számolódik — string drift-guard.
  rentalGrossAmount: LoyaltyAmountSchema,
});
export type LoyaltyRedeemReserveRequest = z.infer<typeof LoyaltyRedeemReserveRequestSchema>;

// A `LOYALTY_REDEEM` pricing-sor — a PricingBreakdown.lines[]-be illeszthető külön kind.
export const LoyaltyRedeemLineSchema = z.object({
  kind: z.literal('LOYALTY_REDEEM'),
  pointsRequested: z.number().int().nonnegative(),
  // A ténylegesen alkalmazott pont (50%-cap + egyenleg után vágva).
  pointsApplied: z.number().int().nonnegative(),
  // A kedvezmény HUF összege (levonás) — STRING drift-guard.
  amount: LoyaltyAmountSchema,
  // A partner pont-egyenlege a (preview-beli) spend UTÁN.
  balanceAfter: z.number().int().nonnegative(),
  // true ha az 50%-cap visszavágta a kért pontot.
  capReached: z.boolean(),
});
export type LoyaltyRedeemLine = z.infer<typeof LoyaltyRedeemLineSchema>;

// Reserve-preview válasz: { data } — `null` ha a partner nem válthat be
// (0 pont, nem jogosult, vagy 0-ra vágott effektív összeg → a csúszka rejtve).
export const LoyaltyRedeemReserveResponseSchema = z.object({
  data: LoyaltyRedeemLineSchema.nullable(),
});
export type LoyaltyRedeemReserveResponse = z.infer<typeof LoyaltyRedeemReserveResponseSchema>;

// Commit-kori spend payload (PR-2 consumer) — a spend-idempotency a VÉGLEGES `rentalId`-re
// kulcsolódik; a `draftId` carry-over az audit/reconcile-hoz (reserve↔commit párosítás).
export const LoyaltyRedeemCommitSchema = z.object({
  rentalId: z.string().uuid(),
  partnerId: z.string().uuid(),
  draftId: z.string().min(1),
  pointsApplied: z.number().int().positive(),
  amount: LoyaltyAmountSchema,
});
export type LoyaltyRedeemCommit = z.infer<typeof LoyaltyRedeemCommitSchema>;
